import { Injectable } from "@angular/core";
import { Action, Actions, Selector, State, StateContext, Store, ofAction } from "@ngxs/store";
import { patch, updateItem } from "@ngxs/store/operators";
import { Observable } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import { LaborOrder } from "src/app/core/interfaces/labor-order.interface";
import { AdminPagesStateDefaults, SetError } from "src/app/features/admin/admin.state";
import { UsersState } from "src/app/features/users/state/users.state";
import { FilterAction } from "src/app/shared/components/filters/filters.component";
import { NavigationItem } from "src/app/shared/interfaces/navigation-item.interface";
import { SetFilter } from "src/app/shared/states/prefill-data/prefill-data.actions";
import { withFlag } from "src/app/shared/states/with-flag.operator";
import { PAG_MAX_RESULTS, getFilterData, getUpdatedItems, mapTimesheet, mapTimesheetRow, mapTotalHoursTimesheet } from "src/app/shared/utils/utils";
import { TimeSheet } from "../../../../core/interfaces/timesheet.interface";
import { TimeSheetRow } from "../../interfaces/timesheet-row.model";
import { TotalHoursValue } from "../../interfaces/total-hours-values.model";
import { TimesheetsService } from "../../services/timesheets.service";
import {
  AddTimeSheet,
  AdjustTimeSheet,
  AssociateTimesheetToOrder,
  CheckTimeSheetError,
  ClearErrorUpdating,
  ClearRowAdded,
  ClearTimeSheet,
  CreateTimesheetRow,
  DuplicateTimeSheet,
  DuplicateMultipleTimesheets,
  GetLaborOrderWithTimeSheets,
  GetTimeSheetById,
  GetTimesheetDetails,
  LoadTimeSheets,
  LoadTimeSheetsGroupedByLaborOrder,
  LoadTimekeepers,
  OfferAssociateTimesheetToOrder,
  RecoverTimeSheet,
  RemoveTimeSheet,
  RemoveTimeSheetRow,
  ResetFilterByOrder,
  ResetTimeSheetRows,
  ResetTimeheetsState,
  RowAddedOrRemoved,
  SetLaborOrderTimesheets,
  SetTimeSheetListToStatusApproved,
  SetTimeSheetListToStatusPendingApproval,
  SetTimeSheetListToStatusUnderReview,
  SetTimeSheetRowStatusDraft,
  SetTimeSheetRowStatusRejected,
  SetTimeSheetStatusApproved,
  SetTimeSheetStatusPendingApproval,
  SetTimeSheetStatusPendingReapproval,
  SetTimeSheetStatusRejected,
  SetTimeSheetStatusUnderReview,
  SetTimesheetTabs,
  UpdateCurrentGang,
  UpdateFilterByOrder,
  UpdateFilterByTimesheet,
  UpdateTimeSheet,
  UpdateTimesheetRows
} from "./timesheets.actions";
import { TimeheetsStateModel } from "./timesheets.model";

export const defaultState = {
  ...AdminPagesStateDefaults,
  removing: false,
  updating: null,
  adding: false,
  rowAdded: false,
  timesheetRows: [],
  errorUpdating: null,
  errorLoading: false,
  calculatedTotalAmount: null,
  hourRateTotals: null,
  totalTimesheets: null,
  itemsByLaborOrder: [],
  filterByOrder: null,
  filterByTimesheet: null,
  currentGang: '',
  timesheetTabs: [],
  timeKeepers: []
};

@State<TimeheetsStateModel>({
  name: "timesheets",
  defaults: defaultState
})
@Injectable()
export class TimeheetsState {
  constructor(
    private TimesheetsService: TimesheetsService,
    private actions$: Actions,
    private store: Store
  ) {}
  isFiltered: false;

  @Selector() static timeSheets(state: TimeheetsStateModel) { return state.items; }
  @Selector() static loading(state: TimeheetsStateModel) { return state.loading; }
  @Selector() static rowAdded(state: TimeheetsStateModel) { return state.rowAdded; }
  @Selector() static updating(state: TimeheetsStateModel) { return state.updating; }
  @Selector() static errorUpdating(state: TimeheetsStateModel) { return state.errorUpdating; }
  @Selector() static errorLoading(state: TimeheetsStateModel) { return state.errorLoading; }
  @Selector() static loaded(state: TimeheetsStateModel) { return state.loaded; }
  @Selector() static saving(state: TimeheetsStateModel) { return state.saving; }
  @Selector() static selectedItem(state: TimeheetsStateModel) { return state.selectedItem; }
  @Selector() static mappedTimesheet(state: TimeheetsStateModel) { return state.mappedTimesheet; }
  @Selector() static timesheetRows(state: TimeheetsStateModel) { return state.timesheetRows; }
  @Selector() static calculatedTotalAmount(state: TimeheetsStateModel) { return state.calculatedTotalAmount; }
  @Selector() static hourRateTotals(state: TimeheetsStateModel) { return state.hourRateTotals; }
  @Selector() static itemsByLaborOrder(state: TimeheetsStateModel) { return state.itemsByLaborOrder; }
  @Selector() static selectedLaborOrder(state: TimeheetsStateModel) { return state.selectedLaborOrder; }
  @Selector() static timeSheetRowErros(state: TimeheetsStateModel) { return state.timeSheetRowErros; }
  @Selector() static filterByOrder(state: TimeheetsStateModel) { return state.filterByOrder; }
  @Selector() static filterByTimesheet(state: TimeheetsStateModel) { return state.filterByTimesheet; }
  @Selector() static filteringByDeleted(state: TimeheetsStateModel) { return state.filterByTimesheet?.items?.GetDeleted; }
  @Selector() static currentGang(state: TimeheetsStateModel) { return state.currentGang; }
  @Selector() static timesheetTabs(state: TimeheetsStateModel) { return state.timesheetTabs; }
  @Selector() static timeKeepers(state: TimeheetsStateModel) { return state.timeKeepers; }

  @Action(LoadTimeSheets)
  load(ctx: StateContext<TimeheetsStateModel>, { skipCount, maxResult, filters }) {
    ctx.patchState({ items: [] });

    const employerForTimekeeper = this.store.selectSnapshot(UsersState.employerForTimekeeper);

    if (employerForTimekeeper) {
      if (filters) {
        filters.items = { ...filters.items, EmployerId: employerForTimekeeper };
      } else {
        filters = {
          items: {
            EmployerId: employerForTimekeeper
          }
        };
      }
    }
    return this.TimesheetsService.getTimeSheets(skipCount, maxResult, filters?.items || {}).pipe(
      withFlag(ctx, "loading", {
        errorAction: (err) => new SetError({ loading: err }),
        onSuccess: (res) => {
          this.isFiltered = filters?.hasEnabledFilters;
          ctx.patchState({
            items: res.items,
            loaded: true,
            totalTimesheets: res.totalCount
          });

          if (!this.isFiltered) {
            const vesselsArray = getFilterData(res, "AvailableVessel");
            ctx.dispatch(new SetFilter("filteredVessels", vesselsArray));

            const timekeepersArray = getFilterData(res, "AvailableTimeKeepers");
            ctx.dispatch(new SetFilter("filteredTimekeepers", timekeepersArray));
          }
        },
        onError: () => ctx.patchState({ items: [], totalTimesheets: 0 })
      }),
      takeUntil(this.actions$.pipe(ofAction(FilterAction)))
    );
  }

  @Action(AddTimeSheet)
  add(ctx: StateContext<TimeheetsStateModel>, { payload, dialogCallback }) {
    return this.TimesheetsService.create(payload).pipe(
      // `rethrow: false` keeps the pre-existing behaviour: this handler used to call
      // `.subscribe()` directly, so the failure never reached NGXS.
      withFlag(ctx, "saving", {
        rethrow: false,
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (response) => {
          dialogCallback(true);
          ctx.dispatch(new OfferAssociateTimesheetToOrder(response.id, null, true));
          ctx.dispatch(new LoadTimeSheets(0, PAG_MAX_RESULTS));
        }
      })
    );
  }

  @Action(UpdateTimeSheet)
  update(ctx: StateContext<TimeheetsStateModel>, { payload, callback }) {
    return this.TimesheetsService.update(payload).pipe(
      withFlag(ctx, "saving", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (response) => {
          const state = ctx.getState();
          if (state.selectedLaborOrder) {
            const laborOrder: LaborOrder = JSON.parse(state.selectedLaborOrder);
            if (laborOrder) {
              const timesheetIndex = laborOrder.timesheets?.findIndex(e => e.id === payload.id);
              if (timesheetIndex != -1 && laborOrder.timesheets[timesheetIndex]) {
                laborOrder.timesheets[timesheetIndex].gang = payload.gang;
                ctx.patchState({
                  selectedLaborOrder: JSON.stringify(laborOrder)
                })
              }
            }
          }
          ctx.patchState({
            items: getUpdatedItems(response, state.items),
            selectedItem: JSON.stringify(response)
          });
        },
        callback
      })
    );
  }

  @Action(RemoveTimeSheet)
  remove(ctx: StateContext<TimeheetsStateModel>, { timesheetId, reason, callbackSuccess, callbackError }) {
    return this.TimesheetsService.remove(timesheetId, reason).then(
      (response) => {
        callbackSuccess();
        const state = ctx.getState();
        const updatedTimeSheets = state.items.filter(item => {
          return item.id !== timesheetId;
        });
        ctx.patchState({ items: updatedTimeSheets });
      },
      (error) => {
        callbackError();
        ctx.dispatch(new SetError({ removing: error }));
      }
    );
  }

  @Action(RecoverTimeSheet)
  recoverTimeSheet(ctx: StateContext<TimeheetsStateModel>, action: RecoverTimeSheet) {
    return this.TimesheetsService.recover(action.timesheetId).pipe(
      tap(
        (response) => {
          action.callbackSuccess();
          const state = ctx.getState();
          const updatedTimeSheets = state.items.filter(item => {
            return item.id !== action.timesheetId;
          });
          ctx.patchState({ items: updatedTimeSheets });
        },
        (error) => {
          action.callbackError();
          ctx.dispatch(new SetError({ removing: error }));
        }
      )
    );
  }

  @Action(RemoveTimeSheetRow)
  removeRow(ctx: StateContext<TimeheetsStateModel>, action: RemoveTimeSheetRow) {
    return this.TimesheetsService.removeRow(action.timesheetId, action.rowIds).pipe(
      withFlag(ctx, "removing", {
        onSuccess: () => ctx.dispatch(new RowAddedOrRemoved()),
        callback: action.callback
      })
    );
  }

  @Action(CreateTimesheetRow)
  createTimesheetRow(ctx: StateContext<TimeheetsStateModel>, { timesheetId, row, callback }) {
    ctx.patchState({ rowAdded: false });
    return this.TimesheetsService.updateOrCreateTimeSheetRows(timesheetId, [row]).pipe(
      withFlag(ctx, "adding", {
        onSuccess: () => {
          ctx.dispatch(new RowAddedOrRemoved());
          ctx.patchState({ rowAdded: true });
          callback();
        }
      })
    );
  }

  queue: Observable<any>[] = [];
  queueRows = [];
  @Action(UpdateTimesheetRows)
  updateOrCreateTimeSheetRows(ctx: StateContext<TimeheetsStateModel>, { timeSheetId, rows, callback }) {
    const request$ = this.TimesheetsService.updateOrCreateTimeSheetRows(timeSheetId, rows);
    let newRows = [];
    let totalAmount;
    let Error: any = null;
    let hourRateTotals: TotalHoursValue;

    //Push items to the queue
    this.queue.push(request$);
    this.queueRows.push(rows);
    // console.log("ADDING 1 REQUEST TO QUEUE: ", this.queue.length + " REMAINING");

    ctx.patchState({ errorUpdating: null });
    const runQueue = () => {
      // console.log("RUNNING QUEUE for ", this.queue.length);
      ctx.patchState({ updating: true });
      if (this.queue.length) {
        return this.queue[0].pipe(
          tap(
            (response) => {
              this.queue.shift();
              // console.log("PUT FINISHED", this.queue.length + " REMAINING", "ROWS: ", response);
              try {
                newRows = response.rows;
                totalAmount = response.calculatedTotalAmount;
                hourRateTotals = mapTotalHoursTimesheet(response.hourRateTotals);
                callback();
              } finally {
                // The queue has to keep draining even when mapping or the consumer
                // callback throws, otherwise `updating` stays true and the grid locks.
                runQueue();
              }
            },
            (error) => {
              Error = error;
              this.queue.shift();
              this.queueRows.shift();
              try {
                callback(error);
              } finally {
                runQueue();
              }
            }
          )
        ).toPromise();
      } else {
        // Queue has no more items, we process the results
        // console.log("==== end of queue ====");
        // Released before the result mapping below, which reads `mappedTimesheet`
        // and can throw on a malformed payload.
        ctx.patchState({ updating: false });
        if (Error) {
          return ctx.patchState({ errorUpdating: Error });
        }

        const state = ctx.getState();
        const currentTimesheet = JSON.parse(state.mappedTimesheet);
        let rowsToBeUpdated = [];

        //This has to be explained...
        // First loop the queue to get the new rows that were added on each iteration for updating
        this.queueRows.forEach(rowsToUpdate => {
          // then get the actual rows for each iteration
          rowsToUpdate.forEach(newRow => {
            //Now we have the newRow wich is the response from the api after the update.
            currentTimesheet.rows.forEach(currentRow => {
              // Get the row to be updated by comparing to the ones sent to the queue
              const rowFromResponse = newRows.find(a => currentRow.id === a.id);

              //don't update the other rows, only the one modified
              if (currentRow.id === newRow.id) {
                newRow.calculatedTotalAmount = rowFromResponse.calculatedTotalAmount;
                newRow.highSTRate = rowFromResponse.highSTRate;
                newRow.highExpRate = rowFromResponse.highExpRate;
                newRow.timeSheetEntries = rowFromResponse.timeSheetEntries;
                newRow.jobCode = rowFromResponse.jobCode;
                newRow.worker = rowFromResponse.worker;
                newRow.rowDifferentialId = rowFromResponse.rowDifferentialId;
                newRow.pi = rowFromResponse.pi;
                newRow.npi = rowFromResponse.npi;
                newRow.timeIn = rowFromResponse.timeIn;
                newRow.timeOut = rowFromResponse.timeOut;
                newRow.trainedJobCode = rowFromResponse.trainedJobCode;
                newRow.entriesTotalHours = rowFromResponse.entriesTotalHours;
                newRow = mapTimesheetRow(newRow);
                rowsToBeUpdated.push(newRow);
              }
            });
          });
        });

        //Update each row state
        rowsToBeUpdated.forEach(newRow => {
          ctx.setState(
            patch({
              timesheetRows: updateItem<TimeSheetRow>(row => {
                return row.id === newRow.id;
              }, newRow)
            })
          );
        });

        ctx.patchState({ calculatedTotalAmount: totalAmount, hourRateTotals: hourRateTotals });

        //Reset queueRows value and close the queue
        return (this.queueRows = []);
      }
    };
    //Start running loop if the queue has requests
    if (this.queue.length === 1) {
      // console.log("===>> QUEUE START ===== ");
      runQueue();
    }
  }

  @Action(ClearTimeSheet)
  clearTimeSheet(ctx: StateContext<TimeheetsStateModel>) {
    ctx.patchState({ selectedItem: null });
  }

  @Action(GetTimeSheetById)
  getTimeSheetById(ctx: StateContext<TimeheetsStateModel>, action: GetTimeSheetById) {
    return this.TimesheetsService.getTimeSheetById(action.id).pipe(
      withFlag(ctx, "loading", {
        errorAction: (error) => new SetError({ loading: error }),
        onSuccess: (response) => ctx.patchState({
          selectedItem: JSON.stringify(response),
          currentGang: response.gang,
          loaded: true
        }),
        onError: () => ctx.patchState({ selectedItem: null })
      })
    );
  }

  @Action(GetTimesheetDetails)
  getTimesheetDetails(ctx: StateContext<TimeheetsStateModel>, action: GetTimesheetDetails) {
    return this.TimesheetsService.getTimeSheetDetails(action.id).pipe(
      withFlag(ctx, "loading", {
        onSuccess: (response) => {
          const mappedTimesheet = mapTimesheet(response);
          const mappedRows = response.rows.map(row => mapTimesheetRow(row));

          ctx.patchState({
            mappedTimesheet: JSON.stringify(mappedTimesheet),
            calculatedTotalAmount: mappedTimesheet.calculatedTotalAmount,
            hourRateTotals: mappedTimesheet.hourRateTotals,
            timesheetRows: mappedRows,
            loaded: true
          });
        },
        onError: () => ctx.patchState({ selectedItem: null, errorLoading: true })
      })
    );
  }

  @Action(SetTimeSheetStatusApproved)
  setTimeSheetStatusApproved(ctx: StateContext<TimeheetsStateModel>, { timeSheetId, callback }) {
    return this.TimesheetsService.setTimeSheetApproved(timeSheetId).pipe(
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (timeSheet) => ctx.patchState({
          mappedTimesheet: JSON.stringify(mapTimesheet(timeSheet)),
          selectedItem: JSON.stringify(timeSheet)
        }),
        callback
      })
    );
  }

  @Action(SetTimeSheetStatusPendingApproval)
  setTimeSheetStatusPendingApproval(
    ctx: StateContext<TimeheetsStateModel>,
    { timeSheetId, callback }
  ) {
    return this.TimesheetsService.setTimeSheetPendingApproval(timeSheetId).pipe(
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (timeSheet) => ctx.patchState({
          mappedTimesheet: JSON.stringify(mapTimesheet(timeSheet)),
          selectedItem: JSON.stringify(timeSheet)
        }),
        callback
      })
    );
  }

  @Action(SetTimeSheetStatusPendingReapproval)
  setTimeSheetStatusPendingReapproval(
    ctx: StateContext<TimeheetsStateModel>,
    { timeSheetId, reason, callback }
  ) {
    return this.TimesheetsService.setTimeSheetPendingReapproval(timeSheetId, reason).pipe(
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (timeSheet) => ctx.patchState({
          mappedTimesheet: JSON.stringify(mapTimesheet(timeSheet)),
          selectedItem: JSON.stringify(timeSheet)
        }),
        callback
      })
    );
  }

  @Action(SetTimeSheetStatusRejected)
  setTimeSheetStatusRejected(
    ctx: StateContext<TimeheetsStateModel>,
    { timeSheetId, reason, callback }
  ) {
    return this.TimesheetsService.setTimeSheetRejected(timeSheetId, reason).pipe(
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (timeSheet) => ctx.patchState({
          mappedTimesheet: JSON.stringify(mapTimesheet(timeSheet)),
          selectedItem: JSON.stringify(timeSheet)
        }),
        callback
      })
    );
  }

  @Action(SetTimeSheetRowStatusRejected)
  setTimeSheetRowStatusRejected(
    ctx: StateContext<TimeheetsStateModel>,
    { timeSheetId, updateModel, callback }
  ) {
    const state = ctx.getState();
    return this.TimesheetsService.setTimeSheetRowRejected(timeSheetId, updateModel).pipe(
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (timeSheetRow) => {
          const mappedTimeSheetRow = mapTimesheetRow(timeSheetRow);
          let mappedTimesheet = JSON.parse(state.mappedTimesheet);
          mappedTimesheet.rows?.map(r => {
            if (r.id === timeSheetRow.id) return mappedTimeSheetRow;
            else return r;
          });
          ctx.patchState({
            mappedTimesheet: JSON.stringify(mappedTimesheet),
            timesheetRows: getUpdatedItems(mappedTimeSheetRow, state.timesheetRows)
          });
        },
        callback
      })
    );
  }

  @Action(SetTimeSheetRowStatusDraft)
  setTimeSheetRowStatusDraft(
    ctx: StateContext<TimeheetsStateModel>,
    { timeSheetId, updateModel, callback }
  ) {
    const state = ctx.getState();
    return this.TimesheetsService.setTimeSheetRowDraft(timeSheetId, updateModel).pipe(
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (timeSheetRow) => {
          let mappedTimesheet = JSON.parse(state.mappedTimesheet);
          mappedTimesheet.rows?.map(r => {
            if (r.id === timeSheetRow.id) return mapTimesheetRow(timeSheetRow);
            else return r;
          });
          ctx.patchState({
            mappedTimesheet: JSON.stringify(mappedTimesheet),
            timesheetRows: getUpdatedItems(mapTimesheetRow(timeSheetRow), state.timesheetRows)
          });
        },
        callback
      })
    );
  }

  @Action(SetTimeSheetStatusUnderReview)
  setTimeSheetStatusUnderReview(
    ctx: StateContext<TimeheetsStateModel>,
    { timeSheetId, callback }
  ) {
    return this.TimesheetsService.setTimeSheetUnderReview(timeSheetId).pipe(
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (timeSheet) => ctx.patchState({
          mappedTimesheet: JSON.stringify(mapTimesheet(timeSheet)),
          selectedItem: JSON.stringify(timeSheet)
        }),
        callback
      })
    );
  }

  @Action(ResetTimeSheetRows)
  resetTimeSheetRows(ctx: StateContext<TimeheetsStateModel>, {}) {
    ctx.patchState({ timesheetRows: [], mappedTimesheet: null });
  }

  @Action(LoadTimeSheetsGroupedByLaborOrder)
  loadTimeSheetsGroupedByLaborOrder(ctx: StateContext<TimeheetsStateModel>, action: LoadTimeSheetsGroupedByLaborOrder) {
    ctx.patchState({ items: [] });

    const employerForTimekeeper = this.store.selectSnapshot(UsersState.employerForTimekeeper);
    if (employerForTimekeeper) {
      if (action.filters) {
        action.filters.items = {
          ...action.filters.items,
          EmployerId: employerForTimekeeper
        };
      } else {
        action.filters = {
          items: {
            EmployerId: employerForTimekeeper
          }
        };
      }
    }

    return this.TimesheetsService.getTimeSheetsGroupedByLaborOrder(action.skipCount, action.maxResult, action.filters?.items || {}).pipe(
      withFlag(ctx, "loading", {
        errorAction: (err) => new SetError({ loading: err }),
        onSuccess: (res) => {
          this.isFiltered = action.filters?.hasEnabledFilters;

          ctx.patchState({
            itemsByLaborOrder: res.items,
            loaded: true,
            totalTimesheets: res.totalCount
          });
        },
        onError: () => ctx.patchState({ itemsByLaborOrder: [], totalTimesheets: 0 })
      }),
      takeUntil(this.actions$.pipe(ofAction(FilterAction)))
    );
  }

  @Action(SetLaborOrderTimesheets)
  setLaborOrderTimesheets(ctx: StateContext<TimeheetsStateModel>, { item }) {
    ctx.patchState({ selectedLaborOrder: JSON.stringify(item) });
  }

  @Action(GetLaborOrderWithTimeSheets)
  getLaborOrderWithTimeSheets(ctx: StateContext<TimeheetsStateModel>, { orderId }) {
    return this.TimesheetsService.getLaborOrderWithTimeSheets(orderId).pipe(
      withFlag(ctx, "loading", {
        errorAction: (error) => new SetError({ loading: error }),
        onSuccess: (response) => ctx.patchState({
          selectedLaborOrder: JSON.stringify(response),
          loaded: true
        }),
        onError: () => ctx.patchState({ selectedLaborOrder: null })
      })
    );
  }

  @Action(CheckTimeSheetError)
  checkTimeSheetError(ctx: StateContext<TimeheetsStateModel>, { timeSheetId }) {
    ctx.patchState({ timeSheetRowErros: null });
    return this.TimesheetsService.checkTimeSheetError(timeSheetId).pipe(
      withFlag(ctx, "loading", {
        errorAction: (err) => new SetError({ loading: err }),
        onSuccess: (res) => {
          res.forEach(r => {
            r.row = mapTimesheetRow(r.row);
            r.conflictingRow = r.conflictingRow ? mapTimesheetRow(r.conflictingRow) : null;
          });
          ctx.patchState({
            timeSheetRowErros: JSON.stringify(res),
            loaded: true
          });
        },
        onError: () => ctx.patchState({ timeSheetRowErros: null })
      })
    );
  }

  @Action(AdjustTimeSheet)
  adjustTimeSheet(ctx: StateContext<TimeheetsStateModel>, { timeSheetId, callback }) {
    return this.TimesheetsService.adjustTimeSheet(timeSheetId).pipe(
      withFlag(ctx, "loading", {
        errorAction: (err) => new SetError({ loading: err }),
        onSuccess: (response) => {
          ctx.dispatch(new OfferAssociateTimesheetToOrder(response.id, response.laborOrder, false));
          ctx.patchState({
            selectedItem: JSON.stringify(response),
            loaded: true
          });
          callback({ timeSheetId: response.id });
        },
        onError: (err) => {
          ctx.patchState({ selectedItem: null });
          callback(err);
        }
      })
    );
  }

  @Action(ResetTimeheetsState)
  ResetTimeheetsState(ctx: StateContext<TimeheetsStateModel>, {}) {
    ctx.setState(defaultState);
  }

  @Action(DuplicateTimeSheet)
  duplicateTimeSheet(ctx: StateContext<TimeheetsStateModel>, { timeSheetId, callback }) {
    return this.TimesheetsService.duplicateTimeSheet(timeSheetId).pipe(
      // Used to call `.subscribe()` directly, so the failure never reached NGXS.
      withFlag(ctx, "saving", {
        rethrow: false,
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (response) => {
          ctx.dispatch(new OfferAssociateTimesheetToOrder(response.id, response.laborOrder, false));
          ctx.dispatch(new LoadTimeSheets(0, PAG_MAX_RESULTS));
          callback({ timeSheetId: response.id });
        }
      })
    );
  }

  @Action(DuplicateMultipleTimesheets)
  duplicateMultipleTimesheets(ctx: StateContext<TimeheetsStateModel>, { timeSheetIds, laborOrderId, callback, errorCallback }) {
    return this.TimesheetsService.duplicateMultipleTimesheets(timeSheetIds, laborOrderId).toPromise().then(
      (response) => {
        callback(response);
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        } else {
          callback(error);
        }
      }
    );
  }

  @Action(SetTimeSheetListToStatusPendingApproval)
  setTimeSheetListToStatusPendingApproval(ctx: StateContext<TimeheetsStateModel>, { orderId, timeSheetIds, callback }) {
    const state = ctx.getState();
    return this.TimesheetsService.setTimeSheetListToPendingApproval(timeSheetIds).pipe(
      // `callback` carries a payload on success, so it is invoked from the handlers rather
      // than through `withFlag`'s `callback`. Both handlers are isolated all the same.
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (response) => {
          const foundOrder = JSON.parse(
            JSON.stringify(state.itemsByLaborOrder.find(o => o.id === orderId))
          );

          response.timeSheets.forEach(element => {
            const foundTimeSheet = foundOrder.timesheets.find(t => t.id === element.id);
            foundTimeSheet.currentStatus = element.currentStatus;
          });
          ctx.patchState({
            itemsByLaborOrder: getUpdatedItems(foundOrder, state.itemsByLaborOrder)
          });
          callback(!response.message ? null : { message: response.message });
        },
        onError: (error) => callback(error)
      })
    );
  }

  @Action(SetTimeSheetListToStatusUnderReview)
  setTimeSheetListToStatusUnderReview(ctx: StateContext<TimeheetsStateModel>, { orderId, timeSheetIds, callback }) {
    const state = ctx.getState();
    return this.TimesheetsService.setTimeSheetListToUnderReview(timeSheetIds).pipe(
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (response) => {
          const foundOrder = JSON.parse(
            JSON.stringify(state.itemsByLaborOrder.find(o => o.id === orderId))
          );

          response.timeSheets.forEach(element => {
            const foundTimeSheet = foundOrder.timesheets.find(t => t.id === element.id);
            foundTimeSheet.currentStatus = element.currentStatus;
          });
          ctx.patchState({
            itemsByLaborOrder: getUpdatedItems(foundOrder, state.itemsByLaborOrder)
          });
          callback(!response.message ? null : { message: response.message });
        },
        onError: (error) => callback(error)
      })
    );
  }

  @Action(SetTimeSheetListToStatusApproved)
  setTimeSheetListToStatusApproved(ctx: StateContext<TimeheetsStateModel>, { orderId, timeSheetIds, callback }) {
    const state = ctx.getState();
    return this.TimesheetsService.setTimeSheetListToApproved(timeSheetIds).pipe(
      withFlag(ctx, "updating", {
        errorAction: (error) => new SetError({ saving: error }),
        onSuccess: (response) => {
          const foundOrder = JSON.parse(
            JSON.stringify(state.itemsByLaborOrder.find(o => o.id === orderId))
          );

          response.timeSheets.forEach(element => {
            const foundTimeSheet = foundOrder.timesheets.find(t => t.id === element.id);
            foundTimeSheet.currentStatus = element.currentStatus;
          });
          ctx.patchState({
            itemsByLaborOrder: getUpdatedItems(foundOrder, state.itemsByLaborOrder)
          });
          callback(!response.message ? null : { message: response.message });
        },
        onError: (error) => callback(error)
      })
    );
  }

  @Action(ClearErrorUpdating)
  clearErrorUpdating(ctx: StateContext<TimeheetsStateModel>) {
    ctx.patchState({ errorUpdating: null });
  }

  @Action(ClearRowAdded)
  clearRowAdded(ctx: StateContext<TimeheetsStateModel>) {
    ctx.patchState({ rowAdded: false });
  }

  @Action(UpdateFilterByOrder)
  updateFilterByOrder(ctx: StateContext<TimeheetsStateModel>, { value }) {
    ctx.patchState({ filterByOrder: value });
  }

  @Action(ResetFilterByOrder)
  resetFilterByOrder(ctx: StateContext<TimeheetsStateModel>) {
    ctx.patchState({ filterByOrder: null });
  }

  @Action(UpdateFilterByTimesheet)
  updateFilterByTimesheet(ctx: StateContext<TimeheetsStateModel>, { value }) {
    ctx.patchState({ filterByTimesheet: value });
  }

  @Action(OfferAssociateTimesheetToOrder)
  offerAssociateTimesheetToOrder(ctx: StateContext<TimeheetsStateModel>, { timesheetId, laborOrder, fromList }) {
    this.TimesheetsService.openModalAssociateTimesheetAndLaborOrder(timesheetId, laborOrder, fromList);
  }

  @Action(AssociateTimesheetToOrder)
  associateTimesheetToOrder(ctx: StateContext<TimeheetsStateModel>, { timesheetId, laborOrderId, fromList, callbackSuccess, callbackError }) {
    return this.TimesheetsService.associateTimesheetToLaborOrder(timesheetId, laborOrderId).toPromise().then(
      (response) => {
        const state = ctx.getState();
        callbackSuccess(response);
        if (fromList) {
          let timesheets: Array<TimeSheet> = JSON.parse(JSON.stringify(state.items));
          const timesheetIndex: number = timesheets.findIndex((t) => { return t.id === response.id; });
          if (timesheetIndex) {
            timesheets[timesheetIndex].laborOrder = response.laborOrder;
            ctx.patchState({ items: timesheets });
          }
        } else {
          let timesheet: TimeSheet = JSON.parse(state.selectedItem);
          if (timesheet) {
            timesheet.laborOrder = response.laborOrder;
            ctx.patchState({ selectedItem: JSON.stringify(timesheet) });
          }
        }
      },
      (error) => {
        callbackError(error);
      }
    )
  }

  @Action(UpdateCurrentGang)
  updateCurrentGang(ctx: StateContext<TimeheetsStateModel>, action: UpdateCurrentGang) {
    ctx.patchState({
      currentGang: action.gang
    });
  }

  @Action(SetTimesheetTabs)
  setTimesheetTabs(ctx: StateContext<TimeheetsStateModel>, action: SetTimesheetTabs) {
    const laborOrder: string = ctx.getState().selectedLaborOrder;
    
    if (!laborOrder) {
      ctx.patchState({ timesheetTabs: [] });
      return;
    }

    const currentGang: string = ctx.getState().currentGang;
    const parsedLaborOrder = JSON.parse(laborOrder);
    
    if (!parsedLaborOrder?.timesheets) {
      ctx.patchState({ timesheetTabs: [] });
      return;
    }

    const tabs: NavigationItem[] = parsedLaborOrder.timesheets.map((t, i) => {
      const isSelectedTimesheet: boolean = action.currentTimesheetId === t.id;
			return {
				text: isSelectedTimesheet ? (currentGang || t.gang || `Timesheet #${i + 1}`) : (t.gang || `Timesheet #${i + 1}`),
				routerLink: [t.id]
			};
		});

    ctx.patchState({
      timesheetTabs: tabs
    });
  }

  @Action(LoadTimekeepers)
  loadTimekeepers(ctx: StateContext<TimeheetsStateModel>, action: LoadTimekeepers) {
    return this.TimesheetsService.getTimekeepers().toPromise().then(
      (response) => {
        ctx.patchState({
          timeKeepers: response.map(e => {
            return {
              value: e.id,
              text: e.userName
            }
          })
        })
      });
  }
}
